import { Injectable, Inject, Injector } from '@angular/core';
import { Http } from '@angular/http';

import { APIClientService } from '../common/api-client.service';
import { AuthService } from '../auth/auth.service';
import { EventsManagerService } from '../common/events-manager.service';
import { ISmartenitConfig } from '../smartenit-config.interface';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { HttpInterceptor } from '../common/http-interceptor.service';
import { AppConfigurationService } from '../common/app-configuration.service';

import 'rxjs/add/operator/do';
import 'rxjs/add/observable/empty';

@Injectable()
export class Oauth2Service extends APIClientService {
  private isRefreshingToken = false;

  constructor(
    http: HttpInterceptor,
    authService: AuthService,
    eventsService: EventsManagerService,
    AppConfiguration: AppConfigurationService,
    injector: Injector
  ) {
    super(
      'oauth2',
      http,
      authService,
      eventsService,
      AppConfiguration,
      injector
    );
    authService.tokenWillExpire.subscribe(() => {
      const subscription = this.refreshToken().subscribe(() => {
        subscription.unsubscribe();
      });
    });
  }

  public logout() {
    this.authService.logout();
    setTimeout(() => {
      this.eventsManagerService.trigger(EventsManagerService.ON_USER_SIGN_OUT);
    }, 1000);
  }

  public login(username: string, password: string): Observable<any> {
    this.authService.clearDB();

    const clientId = this.authService.getClientId();
    const clientSecret = this.authService.getClientSecret();

    if (!clientId || !clientSecret) {
      return Observable.throw(
        new Error('Undefined clientId and clientSecret in AuthService')
      );
    }

    return this.post(
      'token',
      {
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: 'password',
        password,
        username
      },
      { credentials: false }
    ).map((response) => {
      if (response) {
        this.authService.setAccessToken(
          response.access_token,
          response.expires_in
        );
        this.authService.setRefreshToken(response.refresh_token);
        this.authService.setUser(response.data.user);
        this.authService.setAccount(response.data.account);

        this.eventsManagerService.trigger(
          EventsManagerService.ON_USER_SIGN_IN,
          response.data.user
        );
      }

      return response;
    });
  }

  public loginIntegration(provider: string, token: string): Observable<any> {
    this.authService.clearDB();

    const clientId = this.authService.getClientId();
    const clientSecret = this.authService.getClientSecret();

    if (!clientId || !clientSecret) {
      return Observable.throw(
        new Error('Undefined clientId and clientSecret in AuthService')
      );
    }

    return this.post(`integration/${provider}`, {
      client_id: clientId,
      client_secret: clientSecret,
      token
    }).map((response) => {
      if (response) {
        this.authService.setAccessToken(
          response.access_token,
          response.expires_in
        );
        this.authService.setRefreshToken(response.refresh_token);
        this.authService.setUser(response.data.user);
        this.authService.setAccount(response.data.account);

        this.eventsManagerService.trigger(
          EventsManagerService.ON_USER_SIGN_IN,
          response.data.user
        );
      }

      return response;
    });
  }

  public authenticateClient(
    clientId: string,
    clientSecret: string
  ): Observable<any> {
    return this.post(
      'token',
      {
        client_id: clientId,
        client_secret: clientSecret,
        grant_type: 'client_credentials'
      },
      { credentials: false }
    ).map((response) => {
      if (response) {
        this.authService.setAccessToken(
          response.access_token,
          response.expires_in
        );
      }
    });
  }

  public refreshToken(triggerStart: boolean = true): Observable<any> {
    const refreshToken = <any> this.authService.getRefreshToken();

    if (!this.isRefreshingToken || !refreshToken) {
      this.isRefreshingToken = true;

      console.log('Refreshing Token...');

      if (refreshToken) {
        if (triggerStart) {
          this.eventsManagerService.trigger(
            EventsManagerService.ON_TOKEN_REFRESH_START
          );
        }

        const requestData = {
          grant_type: 'refresh_token',
          refresh_token: refreshToken
        };

        return this.post('token', requestData).do(
          (response) => {
            this.isRefreshingToken = false;
            console.log('[SDK Refresh Token] token refreshed');
            this.authService.setAccessToken(
              response.access_token,
              response.expires_in
            );
            this.eventsManagerService.trigger(
              EventsManagerService.ON_TOKEN_REFRESHED,
              {
                newAccessToken: 'Bearer ' + response.access_token,
                status: true
              }
            );
          },
          (error) => {
            this.isRefreshingToken = false;
            this.eventsManagerService.trigger(
              EventsManagerService.ON_TOKEN_REFRESHED,
              { status: false }
            );
            console.log(
              '[SDK refreshToken error] Couldnt refresh token',
              error
            );
            this.eventsManagerService.trigger(
              EventsManagerService.ON_TOKEN_REFRESH_ERROR,
              error
            );
          }
        );
      } else {
        this.isRefreshingToken = false;
        this.eventsManagerService.trigger(
          EventsManagerService.ON_TOKEN_REFRESH_ERROR
        );
        console.log('[SDK refreshToken error] Couldnt refresh token');
        return Observable.empty();
      }
    } else {
      console.log('Taking an existing token refresh request...');
      return this.eventsManagerService.onTokenRefreshed.first();
    }
  }
}
